import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../context/LanguageContext';
import { apiService } from '../services/api';
import { TimeslotInfo, BookAppointmentRequest, UserResponse, ExpressInterestRequest, TimeslotInterestResponse, AppointmentResponse, AppointmentStatus } from '../types/api';
import { useConfirmation } from '../hooks/useConfirmation';
import ConfirmationDialog from './ConfirmationDialog';
import ExpandedDatePicker from './ExpandedDatePicker';
import './BookAppointment.css';

interface BookAppointmentProps {
  appointments: AppointmentResponse[];
  profile: UserResponse | null;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}

const BookAppointment: React.FC<BookAppointmentProps> = ({ appointments, profile, onSuccess, onError }) => {
  const { t, formatDate: formatDateWithLocale, language } = useTranslation();
  
  // State to persist selected date - use localStorage for persistence
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    // Try to get saved date from localStorage first
    const savedDate = localStorage.getItem('bookAppointment_selectedDate');
    return savedDate || '';
  });
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const dateSetByUser = useRef<boolean>(false);
  const [timeslots, setTimeslots] = useState<TimeslotInfo[]>([]);
  const [userInterests, setUserInterests] = useState<TimeslotInterestResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // Use local state for component-specific messages, Dashboard state for booking success
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [isDateAfterSubscription, setIsDateAfterSubscription] = useState<boolean>(false);
  const [disabledReason, setDisabledReason] = useState<string | undefined>(undefined);
  const { confirmation, showConfirmation, hideConfirmation } = useConfirmation();

  // Morning/afternoon booking boundaries, admin-configurable. Seeded with the server's own
  // fallback so the grid renders sensibly before the settings request lands.
  const [bookingBoundaries, setBookingBoundaries] = useState<{ morningCutoff: string; afternoonStart: string }>({
    morningCutoff: '15:00',
    afternoonStart: '15:00',
  });

  // Refs to prevent duplicate API calls
  const interestsFetchedRef = useRef(false);
  const timeslotsFetchingRef = useRef(false);
  const boundariesFetchedRef = useRef(false);

  useEffect(() => {
    if (!interestsFetchedRef.current) {
      fetchUserInterests();
    }
    if (!boundariesFetchedRef.current) {
      fetchBookingBoundaries();
    }
  }, []);

  // Initialize selected date on first load only - never reset after user selection
  useEffect(() => {
    if (isInitialLoad && !selectedDate && !dateSetByUser.current) {
      // Get today's date in local timezone
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const todayString = `${year}-${month}-${day}`;
      
      // If we have a subscription, check if today is valid
      if (profile?.subscription) {
        const subscription = profile.subscription;
        const subscriptionStartDate = subscription.startDate;
        const subscriptionEndDate = subscription.actualEndDate || subscription.endDate;
        const subscriptionStart = new Date(subscriptionStartDate).toISOString().split('T')[0];
        const subscriptionEnd = new Date(subscriptionEndDate).toISOString().split('T')[0];
        
        // Only adjust if today is outside subscription period
        if (todayString < subscriptionStart || todayString > subscriptionEnd) {
          // Set to the later of today or subscription start date
          const validStartDate = subscriptionStart > todayString ? subscriptionStart : todayString;
          // But make sure it's not after subscription end date
          if (validStartDate <= subscriptionEnd) {
            setSelectedDate(validStartDate);
            localStorage.setItem('bookAppointment_selectedDate', validStartDate);
          } else if (todayString <= subscriptionEnd) {
            setSelectedDate(todayString);
            localStorage.setItem('bookAppointment_selectedDate', todayString);
          } else {
            // If today is after subscription end, use today to show warning
            setSelectedDate(todayString);
            localStorage.setItem('bookAppointment_selectedDate', todayString);
          }
        } else {
          // Today is within subscription period, use it
          setSelectedDate(todayString);
          localStorage.setItem('bookAppointment_selectedDate', todayString);
        }
      } else {
        // No subscription, use today
        setSelectedDate(todayString);
        localStorage.setItem('bookAppointment_selectedDate', todayString);
      }
      
      setIsInitialLoad(false);
    }
  }, [profile?.subscription?.startDate, profile?.subscription?.endDate, profile?.subscription?.actualEndDate, selectedDate, isInitialLoad]);

  useEffect(() => {
    if (selectedDate) { // Only fetch when we have a selected date
      checkSubscriptionAndFetchTimeslots();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, profile?.subscription?.startDate, profile?.subscription?.endDate, profile?.subscription?.actualEndDate]);


  const fetchUserInterests = async () => {
    if (interestsFetchedRef.current) return;
    
    try {
      interestsFetchedRef.current = true;
      const response = await apiService.getMyInterests();
      if (response.success && response.data) {
        setUserInterests(response.data);
      }
    } catch (err: any) {
      console.error('Error fetching user interests:', err);
      interestsFetchedRef.current = false; // Reset on error to allow retry
    }
  };


  const fetchBookingBoundaries = async () => {
    if (boundariesFetchedRef.current) return;

    try {
      boundariesFetchedRef.current = true;
      const response = await apiService.getCancellationDeadlineSettings();
      if (response.success && response.data) {
        setBookingBoundaries({
          morningCutoff: response.data.morningClientBookingCutoffTime,
          afternoonStart: response.data.afternoonClientBookingStartTime,
        });
      }
    } catch (err: any) {
      // Keep the defaults - the server enforces the real boundaries on booking either way.
      console.error('Error fetching booking boundaries:', err);
      boundariesFetchedRef.current = false; // Reset on error to allow retry
    }
  };


  const checkSubscriptionAndFetchTimeslots = () => {
    if (!profile?.subscription) {
      setIsDateAfterSubscription(false);
      fetchAvailableTimeslots();
      return;
    }

    const subscription = profile.subscription;
    const subscriptionStartDate = subscription.startDate;
    const subscriptionEndDate = subscription.actualEndDate || subscription.endDate;
    
    // Convert ISO dates to YYYY-MM-DD format for comparison
    const subscriptionStartDateStr = subscriptionStartDate.split('T')[0];
    const subscriptionEndDateStr = subscriptionEndDate.split('T')[0];
    
    // Compare date strings directly (YYYY-MM-DD format allows direct string comparison)
    if (selectedDate < subscriptionStartDateStr || selectedDate > subscriptionEndDateStr) {
      setIsDateAfterSubscription(true);
      setTimeslots([]);
    } else {
      setIsDateAfterSubscription(false);
      fetchAvailableTimeslots();
    }
  };

  const fetchAvailableTimeslots = async (preserveMessages = false) => {
    // Prevent duplicate calls for the same date
    if (timeslotsFetchingRef.current) {
      return;
    }
    
    try {
      timeslotsFetchingRef.current = true;
      
      // Only set loading and clear error if not preserving messages
      if (!preserveMessages) {
        setIsLoading(true);
        setError('');
      }
      
      const response = await apiService.getTimeslots(selectedDate);
      if (response.success && response.data) {
        setDisabledReason(response.data.isFullyDisabled ? response.data.disabledReason : undefined);
        let allSlots = response.data.timeslots;

        // If the selected date is today, filter out past timeslots
        const today = new Date().toISOString().split('T')[0];
        if (selectedDate === today) {
          const now = new Date();
          const currentTime = now.getHours() * 60 + now.getMinutes(); // Convert to minutes
          
          allSlots = allSlots.filter(slot => {
            const [hours, minutes] = slot.startTime.split(':').map(Number);
            const slotTime = hours * 60 + minutes;
            return slotTime > currentTime; // Only show future timeslots
          });
        }

        // Combine available and full timeslots into one list, maintaining chronological order
        // We need to show timeslots that are full (for expressing interest) even if isAvailable is false
        const allDisplaySlots = allSlots.filter(slot => {
          // Don't show disabled timeslots or when gym is closed
          if (slot.isDisabled === true || slot.isGymOpen === false) {
            return false;
          }
          
          // Show available timeslots
          if (slot.isAvailable && slot.currentBookings < slot.capacity) {
            return true;
          }
          
          // Show if it's explicitly available but full
          if (slot.isAvailable && slot.currentBookings >= slot.capacity) {
            return true;
          }
          
          // Also show if it's unavailable due to capacity but the gym is open and not disabled
          // This handles the case where backend sets isAvailable:false for full capacity
          if (!slot.isAvailable && slot.currentBookings >= slot.capacity) {
            return true;
          }
          
          return false;
        });

        // Sort by start time to maintain proper order
        allDisplaySlots.sort((a, b) => {
          const timeA = a.startTime.split(':').map(Number);
          const timeB = b.startTime.split(':').map(Number);
          const minutesA = timeA[0] * 60 + timeA[1];
          const minutesB = timeB[0] * 60 + timeB[1];
          return minutesA - minutesB;
        });

        setTimeslots(allDisplaySlots);
      }
    } catch (err: any) {
      console.error('Error fetching timeslots:', err);
      if (!preserveMessages) {
        setError(t('loadingTimeslots'));
        // Clear error message after 3 seconds
        setTimeout(() => {
          setError('');
        }, 3000);
      }
    } finally {
      if (!preserveMessages) {
        setIsLoading(false);
      }
      timeslotsFetchingRef.current = false;
    }
  };


  const handleBookAppointment = async (timeslot: TimeslotInfo) => {
    // Check for existing active appointments on the selected date using cached data
    const existingAppointment = appointments.find(apt => {
      const aptDateOnly = apt.appointmentDate.split('T')[0]; // Handle ISO format
      return aptDateOnly === selectedDate && apt.status === AppointmentStatus.Active;
    });
    
    if (existingAppointment) {
      // User already has an active appointment on this date
      showConfirmation({
        title: t('bookingNotAllowed'),
        message: t('onlyOneAppointmentPerDay'),
        confirmText: 'Ok',
        cancelText: null, // Hide cancel button
        type: 'warning',
        onConfirm: () => {
          hideConfirmation();
        }
      });
      
      // Auto-dismiss after 3 seconds
      setTimeout(() => {
        hideConfirmation();
      }, 3000);
      return;
    }

    // Check subscription limit (including admin-booked appointments)
    if (profile?.subscription) {
      const monthlyLimit = profile.subscription.monthlyLimit;
      const validAppointmentCount = profile.subscription.validAppointmentCount || 0;
      
      if (monthlyLimit > 0 && validAppointmentCount >= monthlyLimit) {
        showConfirmation({
          title: t('bookingNotAllowed'),
          message: t('monthlyLimitReached'),
          confirmText: 'Ok',
          cancelText: null, // Hide cancel button
          type: 'warning',
          onConfirm: () => {
            hideConfirmation();
          }
        });
        
        // Auto-dismiss after 3 seconds
        setTimeout(() => {
          hideConfirmation();
        }, 3000);
        return;
      }
    }

    // No existing appointment found, proceed with booking confirmation
    showConfirmation({
      title: t('bookAppointment'),
      message: `${t('areYouSure')} ${formatTime(timeslot.startTime)} - ${formatTime(timeslot.endTime)}?`,
      confirmText: t('bookAppointment'),
      cancelText: t('cancel'),
      type: 'info',
      onConfirm: () => {
        confirmBookAppointment(timeslot);
      }
    });
  };

  const confirmBookAppointment = async (timeslot: TimeslotInfo) => {
    // Hide confirmation dialog immediately
    hideConfirmation();
    
    // Show loading state in main component
    setIsLoading(true);
    setError('');

    try {
      const bookingData: BookAppointmentRequest = {
        appointmentDate: selectedDate,
        startTime: timeslot.startTime,
        endTime: timeslot.endTime
      };

      const response = await apiService.bookAppointment(bookingData);
      if (response.success) {
        // Show success message in Dashboard (persists across re-renders)
        if (onSuccess) {
          onSuccess(t('appointmentBookedSuccessfully'));
          // Clear success message after 5 seconds
          setTimeout(() => {
            onSuccess('');
          }, 5000);
        }
        
        // Refresh timeslots while preserving the selected date
        fetchAvailableTimeslots(true);
        
        // Trigger a custom event to notify other components to refresh
        window.dispatchEvent(new CustomEvent('appointmentBooked'));
      } else {
        if (onError) {
          onError(response.message || t('appointmentBookingFailed'));
        } else {
          setError(response.message || t('appointmentBookingFailed'));
          // Clear error message after 3 seconds
          setTimeout(() => {
            setError('');
          }, 3000);
        }
      }
    } catch (err: any) {
      if (onError) {
        onError(err.response?.data?.message || t('appointmentBookingFailed'));
      } else {
        setError(err.response?.data?.message || t('appointmentBookingFailed'));
        // Clear error message after 3 seconds
        setTimeout(() => {
          setError('');
        }, 3000);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const hasInterestInTimeslot = (timeslot: TimeslotInfo): TimeslotInterestResponse | null => {
    return userInterests.find(interest => 
      interest.appointmentDate === selectedDate &&
      interest.startTime === timeslot.startTime &&
      interest.endTime === timeslot.endTime
    ) || null;
  };

  const handleExpressInterest = async (timeslot: TimeslotInfo) => {
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const interestData: ExpressInterestRequest = {
        appointmentDate: selectedDate,
        startTime: timeslot.startTime,
        endTime: timeslot.endTime
      };

      const response = await apiService.expressInterest(interestData);
      if (response.success) {
        setSuccess(t('interestExpressed') + '! ' + t('notificationWhenSpotAvailable'));
        fetchUserInterests(); // Refresh user interests
        
        // Clear success message after 4 seconds
        setTimeout(() => {
          setSuccess('');
        }, 4000);
      } else {
        setError(response.message || t('error'));
        // Clear error message after 3 seconds
        setTimeout(() => {
          setError('');
        }, 3000);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('error'));
      // Clear error message after 3 seconds
      setTimeout(() => {
        setError('');
      }, 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveInterest = async (interestId: number) => {
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await apiService.removeInterest(interestId);
      if (response.success) {
        setSuccess(t('removeInterest') + ' ' + t('success') + '.');
        fetchUserInterests(); // Refresh user interests
        
        // Clear success message after 4 seconds
        setTimeout(() => {
          setSuccess('');
        }, 4000);
      } else {
        setError(response.message || t('error'));
        // Clear error message after 3 seconds
        setTimeout(() => {
          setError('');
        }, 3000);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('error'));
      // Clear error message after 3 seconds
      setTimeout(() => {
        setError('');
      }, 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (timeString: string) => {
    const parts = timeString.split(':');
    if (parts.length >= 2) {
      const hours = parseInt(parts[0]);
      const minutes = parts[1];
      return `${hours.toString().padStart(2, '0')}:${minutes}`;
    }
    return timeString;
  };

  const formatSelectedDateForBookingInfo = (dateString: string): string => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const weekday = date.getDay();
    
    if (language === 'el') { // Greek
      const dayNames = ['Κυριακή', 'Δευτέρα', 'Τρίτη', 'Τετάρτη', 'Πέμπτη', 'Παρασκευή', 'Σάββατο'];
      return `${dayNames[weekday]} ${day}-${month}-${year}`;
    } else { // English
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      return `${dayNames[weekday]} ${day}-${month}-${year}`;
    }
  };

  const getAvailabilityText = (timeslot: TimeslotInfo) => {
    const available = timeslot.capacity - timeslot.currentBookings;
    if (available === timeslot.capacity) {
      if (timeslot.capacity === 1) {
        return `${timeslot.capacity} ${t('spotAvailable')}`;
      }
      return `${timeslot.capacity} ${t('spotsAvailable')}`;
    }
    return `${available} ${t('ofSpotsAvailable')} ${timeslot.capacity} ${t('spotsAvailable')}`;
  };

  const getMinDate = () => {
    const today = new Date().toISOString().split('T')[0];
    
    // If user has a subscription, use the original start date for calendar restrictions
    if (profile?.subscription) {
      const subscription = profile.subscription;
      const subscriptionStartDate = subscription.startDate;
      // Extract just the date part from ISO format and parse in local timezone to avoid timezone issues
      const dateOnly = subscriptionStartDate.split('T')[0];
      const [year, month, day] = dateOnly.split('-').map(Number);
      const startDate = new Date(year, month - 1, day);
      const startYear = startDate.getFullYear();
      const startMonth = String(startDate.getMonth() + 1).padStart(2, '0');
      const startDay = String(startDate.getDate()).padStart(2, '0');
      const subscriptionStart = `${startYear}-${startMonth}-${startDay}`;
      
      // Return the later of today or subscription start date
      return subscriptionStart > today ? subscriptionStart : today;
    }
    
    return today;
  };

  const getMaxDate = () => {
    // If user has a subscription, use the actual end date as the max date
    if (profile?.subscription) {
      const subscription = profile.subscription;
      const subscriptionEndDate = subscription.actualEndDate || subscription.endDate;
      // Extract just the date part from ISO format and parse in local timezone to avoid timezone issues
      const dateOnly = subscriptionEndDate.split('T')[0];
      const [year, month, day] = dateOnly.split('-').map(Number);
      const maxDate = new Date(year, month - 1, day);
      const maxYear = maxDate.getFullYear();
      const maxMonth = String(maxDate.getMonth() + 1).padStart(2, '0');
      const maxDay = String(maxDate.getDate()).padStart(2, '0');
      return `${maxYear}-${maxMonth}-${maxDay}`;
    }
    
    // If no subscription, allow booking up to 30 days in advance
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 30);
    return maxDate.toISOString().split('T')[0];
  };

  // Minutes since midnight, tolerant of both "HH:mm" (settings) and "HH:mm:ss" (timeslots).
  const toMinutesOfDay = (timeString: string): number => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + (minutes || 0);
  };

  // Helper function to check if morning client can book this timeslot
  const canMorningClientBook = (timeslot: TimeslotInfo): boolean => {
    // If user is not a morning client, they can book any timeslot
    if (!profile?.isMorningClient) {
      return true;
    }

    // Morning clients cannot book timeslots starting at or after their cutoff
    return toMinutesOfDay(timeslot.startTime) < toMinutesOfDay(bookingBoundaries.morningCutoff);
  };

  // Helper function to check if afternoon client can book this timeslot
  const canAfternoonClientBook = (timeslot: TimeslotInfo): boolean => {
    // If user is not an afternoon client, they can book any timeslot
    if (!profile?.isAfternoonClient) {
      return true;
    }

    // Saturday has only morning hours, so afternoon clients may book any Saturday slot.
    // Built from parts so the "YYYY-MM-DD" string isn't parsed as UTC midnight.
    const [year, month, day] = selectedDate.split('-').map(Number);
    if (new Date(year, month - 1, day).getDay() === 6) {
      return true;
    }

    // Afternoon clients cannot book timeslots starting before their start time
    return toMinutesOfDay(timeslot.startTime) >= toMinutesOfDay(bookingBoundaries.afternoonStart);
  };

  return (
    <div className="book-appointment">
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      
      <div className="book-appointment-header">
        <h2>{t('bookAppointmentHeader')}</h2>
        <p>{t('bookAppointmentDescription')}</p>
      </div>

      <div className="date-selection">
        {profile?.subscription && (
          <div className="subscription-info">
            <p className="subscription-period">
              📅 {t('subscriptionPeriod')}: {' '}
              {profile.subscription.actualStartDate ? formatDateWithLocale(profile.subscription.actualStartDate, { short: true }) : formatDateWithLocale(profile.subscription.startDate, { short: true })}{' '}
              - {' '}
              {formatDateWithLocale(profile.subscription.actualEndDate || profile.subscription.endDate, { short: true })}
            </p>
            <p className="booking-restriction-info">
              ℹ️ {t('bookingPeriodRestriction')}
            </p>
          </div>
        )}
        <label>{t('selectDate')}:</label>
        <ExpandedDatePicker
          selectedDate={selectedDate}
          onDateChange={(newDate) => {
            setSelectedDate(newDate);
            localStorage.setItem('bookAppointment_selectedDate', newDate);
            dateSetByUser.current = true;
          }}
          minDate={getMinDate()}
          maxDate={getMaxDate()}
          disabled={isLoading}
          appointments={appointments}
        />
      </div>

      {isDateAfterSubscription && profile?.subscription && (
        <div className="subscription-warning">
          <div className="warning-icon">⚠️</div>
          <h3>{t('dateOutsideSubscription')}</h3>
          <p>
            {t('cannotBookOutsideSubscription')}
          </p>
          <p>
            {t('subscriptionValidFrom')}{' '}
            {formatDateWithLocale(profile.subscription.startDate)}{' '}
            {t('to')}{' '}
            {formatDateWithLocale(profile.subscription.actualEndDate || profile.subscription.endDate)}.
          </p>
          <p>{t('selectDateWithinSubscription')}</p>
        </div>
      )}

      {!isDateAfterSubscription && isLoading ? (
        <div className="loading">{t('loadingTimeslots')}</div>
      ) : !isDateAfterSubscription ? (
        <div className="timeslots-section">
          {timeslots.length === 0 ? (
            <div className="no-timeslots">
              {disabledReason && (
                <div className="disabled-reason-banner">
                  <span className="disabled-reason-text">💬 {disabledReason}</span>
                </div>
              )}
              <div className="no-timeslots-icon">📅</div>
              <h3>{t('noTimeSlots')}</h3>
              <p>{t('noTimeSlotsMessage')}</p>
            </div>
          ) : (
            <>
              <h3>{t('availableTimeSlots')}</h3>
              <p className="timeslots-info">{formatSelectedDateForBookingInfo(selectedDate)}</p>
              <div className="timeslots-grid">
                {timeslots.map((timeslot) => {
                  const userInterest = hasInterestInTimeslot(timeslot);
                  const isFull = timeslot.currentBookings >= timeslot.capacity;
                  const canBookMorning = canMorningClientBook(timeslot);
                  const canBookAfternoon = canAfternoonClientBook(timeslot);
                  const isBookable = timeslot.isAvailable && !isFull && canBookMorning && canBookAfternoon;
                  const isRestrictedForMorningClient = profile?.isMorningClient && !canBookMorning;
                  const isRestrictedForAfternoonClient = profile?.isAfternoonClient && !canBookAfternoon;
                  const isTimeRestricted = isRestrictedForMorningClient || isRestrictedForAfternoonClient;

                  return (
                    <div key={timeslot.id} className={`timeslot-card ${isFull ? 'full' : ''} ${timeslot.hasOverride ? 'has-override' : ''} ${isRestrictedForMorningClient ? 'morning-client-restricted' : ''} ${isRestrictedForAfternoonClient ? 'afternoon-client-restricted' : ''}`}>
                      <div className="timeslot-time">
                        {formatTime(timeslot.startTime)} - {formatTime(timeslot.endTime)}
                        {timeslot.hasTimeChange && (
                          <span className="time-changed-indicator" title={t('timeModifiedFromOriginal', 'Time has been modified from original schedule')}>⚡</span>
                        )}
                      </div>
                      
                      {timeslot.hasTimeChange && timeslot.originalStartTime && timeslot.originalEndTime && (
                        <div className="original-time-note">
                          {t('originally')}: {formatTime(timeslot.originalStartTime)} - {formatTime(timeslot.originalEndTime)}
                        </div>
                      )}
                      
                      {timeslot.overrideReason && (
                        <div className="override-reason">
                          {timeslot.overrideReason}
                        </div>
                      )}
                      
                      <div className="timeslot-availability">
                        <span className={`availability-text ${isFull ? 'full' : ''} ${isTimeRestricted ? 'restricted' : ''}`}>
                          {isRestrictedForMorningClient ?
                            t('notAvailableForMorningClients') :
                            isRestrictedForAfternoonClient ?
                              t('notAvailableForAfternoonClients') :
                            isFull ?
                              `${timeslot.currentBookings}/${timeslot.capacity} - ${t('full')}` :
                              getAvailabilityText(timeslot)
                          }
                        </span>
                        <div className="availability-bar">
                          <div 
                            className={`availability-fill ${isFull ? 'full' : ''}`}
                            style={{ 
                              width: isFull ? '100%' : `${((timeslot.capacity - timeslot.currentBookings) / timeslot.capacity) * 100}%` 
                            }}
                          />
                        </div>
                      </div>

                      {/* Show appropriate button based on availability */}
                      {isRestrictedForMorningClient ? (
                        <button
                          className="restricted-button"
                          disabled={true}
                        >
                          {t('restrictedForMorningClients')}
                        </button>
                      ) : isRestrictedForAfternoonClient ? (
                        <button
                          className="restricted-button"
                          disabled={true}
                        >
                          {t('restrictedForAfternoonClients')}
                        </button>
                      ) : isBookable ? (
                        <button
                          type="button"
                          className="book-button"
                          onClick={() => handleBookAppointment(timeslot)}
                          disabled={isLoading}
                        >
                          {isLoading ? t('booking') : t('bookThisSlot')}
                        </button>
                      ) : isFull ? (
                        userInterest ? (
                          <div className="interest-section">
                            <div className="interest-indicator">
                              <span className="interest-icon">⭐</span>
                              <span>{t('interestExpressed')}</span>
                            </div>
                            <button
                              type="button"
                              className="remove-interest-button"
                              onClick={() => handleRemoveInterest(userInterest.id)}
                              disabled={isLoading}
                            >
                              {isLoading ? t('removing') : t('removeInterest')}
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="express-interest-button"
                            onClick={() => handleExpressInterest(timeslot)}
                            disabled={isLoading || isTimeRestricted}
                            title={isRestrictedForMorningClient ? t('morningClientRestriction') : isRestrictedForAfternoonClient ? t('afternoonClientRestriction') : ''}
                          >
                            {isLoading ? t('expressing') : t('expressInterest')}
                          </button>
                        )
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      ) : null}



      <ConfirmationDialog
        isOpen={confirmation.isOpen}
        title={confirmation.title}
        message={confirmation.message}
        confirmText={confirmation.confirmText}
        cancelText={confirmation.cancelText}
        type={confirmation.type}
        onConfirm={confirmation.onConfirm}
        onCancel={confirmation.onCancel}
        isLoading={confirmation.isLoading}
        isSuccess={confirmation.isSuccess}
        successMessage={confirmation.successMessage}
      />
    </div>
  );
};

export default BookAppointment; 